/**
 * Fair Price Status Chip
 *
 * Small absolute-positioned overlay chip on the chart (like the other chart
 * overlays) showing the live Fair Price indicator state:
 *   phase (CONT / REV / DEAD, color-coded) · ARMED vs AT FAIR PRICE ·
 *   distance · active anchor name (929/GLBX/ASIA/PM/NEWS/FAIR PRICE → — session names, not clock times, since ET clock labels confuse a PDT chart) · amber "ATH"
 *   tag while the ATH guard suppresses shorts · live-signal count (when > 0) ·
 *   compact rolling score when the engine sends stats (wins+stops > 0)
 * e.g. "REV · ARMED · 81.5pt · 8PM [ATH] · 1 live · 12W/5S 71%"
 * Lifecycle fields are optional on the payload — older engines simply omit
 * them and the chip renders exactly as before.
 *
 * Renders nothing until the indicator is enabled AND the first engine update
 * has arrived. Styling matches the dark-theme overlay chips (translucent dark
 * pill, subtle border, 10-11px type).
 */

import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  useFairPriceStore,
  FairPricePhaseMode,
  FairPriceAnchorKind,
} from '../store/fairPriceStore';
import { normalizeToBase } from '../utils/symbolUtils';

interface FairPriceStatusChipProps {
  /** Chart symbol — the chip must show THIS symbol's state, not whichever updated last. */
  symbol: string;
}

const MODE_DISPLAY: Record<FairPricePhaseMode, { label: string; color: string }> = {
  continuation: { label: 'CONT', color: '#00e676' },
  reversion: { label: 'REV', color: '#ffb300' },
  dead: { label: 'DEAD', color: '#78909c' },
};

/** Short anchor names matching the on-chart labels (929 kept digits-only for width). */
const ANCHOR_DISPLAY: Record<FairPriceAnchorKind, string> = {
  rth929: '929',
  open6pm: 'GLBX',
  open8pm: 'ASIA',
  open2pm: 'PM',
  prenews: 'NEWS',
  migrated: 'FAIR PRICE →',
};

/**
 * Layout only. Positioning and the glass background come from
 * InfoOverlayStack.css now that the chip lives in the stack — it used to
 * position itself absolutely at top:44/left:12, which put it OUTSIDE the
 * stack's flow and gave it its own slightly-different translucency.
 */
const chipStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.4,
  fontFamily: 'inherit',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

const dotStyle = (color: string): React.CSSProperties => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: color,
  flexShrink: 0,
});

const sepStyle: React.CSSProperties = { color: 'rgba(255, 255, 255, 0.3)' };

/** Small amber tag shown while the ATH guard suppresses shorts. */
const athTagStyle: React.CSSProperties = {
  padding: '1px 4px',
  borderRadius: 3,
  background: 'rgba(255, 179, 0, 0.18)',
  border: '1px solid rgba(255, 179, 0, 0.55)',
  color: '#ffb300',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.5,
  lineHeight: 1.3,
  flexShrink: 0,
};

const FairPriceStatusChip: React.FC<FairPriceStatusChipProps> = ({ symbol }) => {
  const base = normalizeToBase(symbol);
  // Read state for THIS symbol. The shared `state` field holds whichever symbol
  // the engine updated last, which showed ES numbers on an MNQ chart.
  // useShallow: this selector builds a new object each run (repo zustand rule).
  const {
    enabled,
    phase,
    armed,
    distance,
    activeAnchorKind,
    athGuardActive,
    liveSignals,
    stats,
  } = useFairPriceStore(
    useShallow((s) => {
      const st = s.statesBySymbol[base] ?? null;
      return {
        enabled: s.enabled,
        phase: st?.phase ?? null,
        armed: st?.armed ?? false,
        distance: st?.distance ?? null,
        activeAnchorKind: st?.activeAnchorKind ?? null,
        athGuardActive: st?.athGuardActive ?? false,
        liveSignals: st?.liveSignals ?? 0,
        stats: st?.stats ?? null,
      };
    })
  );

  if (!enabled || !phase) return null;

  const mode = MODE_DISPLAY[phase.mode] ?? MODE_DISPLAY.dead;
  const armedLabel = armed ? 'ARMED' : 'AT FAIR PRICE';
  const armedColor = armed ? '#00e676' : '#ef5350';
  const anchorLabel = activeAnchorKind !== null ? ANCHOR_DISPLAY[activeAnchorKind] : null;

  // Lifecycle extras (older engines omit these fields -> both render null).
  const showLive = typeof liveSignals === 'number' && liveSignals > 0;
  const showScore =
    stats !== null &&
    typeof stats.wins === 'number' &&
    typeof stats.stops === 'number' &&
    stats.wins + stats.stops > 0;
  const scoreText = showScore
    ? `${stats.wins}W/${stats.stops}S ${Math.round(
        typeof stats.winRatePct === 'number' ? stats.winRatePct : 0
      )}%`
    : null;

  return (
    <div
      className="fair-price-status-chip"
      style={chipStyle}
      title="Fair Price indicator: phase · distance gate · points from anchor · active anchor (ATH = shorts suppressed near all-time high)"
    >
      <span style={dotStyle(mode.color)} />
      <span style={{ color: mode.color }}>{mode.label}</span>
      <span style={sepStyle}>·</span>
      <span style={{ color: armedColor }}>{armedLabel}</span>
      {distance !== null && Number.isFinite(distance) && (
        <>
          <span style={sepStyle}>·</span>
          <span>{distance.toFixed(1)}pt</span>
        </>
      )}
      {anchorLabel && (
        <>
          <span style={sepStyle}>·</span>
          <span>{anchorLabel}</span>
        </>
      )}
      {athGuardActive && <span style={athTagStyle}>ATH</span>}
      {showLive && (
        <>
          <span style={sepStyle}>·</span>
          <span style={{ color: '#ffd54f' }}>{liveSignals} live</span>
        </>
      )}
      {scoreText && (
        <>
          <span style={sepStyle}>·</span>
          <span>{scoreText}</span>
        </>
      )}
    </div>
  );
};

export default FairPriceStatusChip;
